import React, { useEffect } from 'react';
import {
  MarketingNav, MarketingFooter, IntegrationStrip, ScreenshotFrame,
  MetricStat, SectionHeader, PrimaryCTA, SecondaryCTA,
  InboxMockup, RoutingMockup, BlastMockup, AnalyticsMockup, MobileMockup,
  INTEGRATIONS, BRAND_BLUE
} from '../components/marketing';

const CAPABILITY_GRID = [
  { icon: '💬', title: 'Two-way SMS & MMS', body: 'Send and receive texts, photos, and video with any US mobile number in a shared inbox.' },
  { icon: '👥', title: 'Group messaging',    body: 'Blast ad-hoc group threads and keep every reply threaded to the right contact.' },
  { icon: '📢', title: 'Text Blasts',        body: 'Send personalized broadcasts to segmented lists with opt-out enforced automatically.' },
  { icon: '⏰', title: 'Scheduled sends',    body: 'Queue individual messages or entire blasts to go out at a specific date and time.' },
  { icon: '📝', title: 'Templates',          body: 'Reusable snippets with placeholders your whole team can pull from.' },
  { icon: '🔀', title: 'Routing wizard',     body: 'Dedicated, round-robin, least-loaded, or queue-based routing on every number.' },
  { icon: '📮', title: 'Queues with SLAs',   body: 'Shared queues, SLA alerts, and auto-escalation when a breach is coming.' },
  { icon: '🕐', title: 'Business hours',     body: 'Real business-hours calendar drives after-hours auto-replies and routing.' },
  { icon: '⭐', title: 'VIP & keyword rules', body: 'Send VIPs to a preferred rep, and instant replies to STOP, HELP, HOURS, and custom keywords.' },
  { icon: '🤖', title: 'AI compose',         body: 'One-click AI-drafted replies tuned for SMS length and tone.' },
  { icon: '🛡️', title: 'SHAFT compliance',   body: 'Configurable rules block sex, hate, alcohol, firearms, tobacco, and profanity before send.' },
  { icon: '📊', title: 'Dashboards & AI insights', body: 'Customizable widgets plus AI-generated summaries of volume, sentiment, and response time.' },
  { icon: '📄', title: 'Scheduled PDF reports',   body: 'Auto-delivered reports on any cadence.' },
  { icon: '📱', title: 'Native iOS & Android',    body: 'Companion apps with push notifications and full inbox parity.' },
  { icon: '🔗', title: 'Public REST API',    body: 'Full CRUD for anything the UI does.' },
  { icon: '🔔', title: 'Outbound webhooks',  body: 'Real-time event push on every inbound, outbound, delivery, and status change.' },
];

const INTEGRATION_DETAILS = [
  { name: 'Salesforce',       what: 'Sync contacts both directions and log every SMS as a CRM activity on the matching Lead or Contact.' },
  { name: 'Clio',             what: 'Sync clients and matter contacts; log every SMS as a communication on the file.' },
  { name: 'Meevo',            what: 'Sync client records and appointment reminders for salon and spa operators.' },
  { name: 'ShipStation',      what: 'Send SMS notifications on order confirmation, shipment, and delivery events.' },
  { name: 'Microsoft Teams',  what: 'Route inbound SMS notifications straight into a Teams channel.' },
  { name: 'AI',               what: 'AI-drafted replies, sentiment tags, and the summary insights on the dashboard — powered by a leading language model under the hood.' },
  { name: 'Bandwidth',        what: 'US SMS/MMS carrier delivering every message on the platform.' },
  { name: 'Webhooks',         what: 'Push any platform event to any external system — the escape hatch for custom automation.' },
];

const HOW_IT_WORKS = [
  { n: 1, title: 'Bring a number',    body: 'Port an existing number or spin up a new one — text-enable a landline or provision a fresh 10DLC.' },
  { n: 2, title: 'Invite your team',  body: 'Add agents, group them into teams, and set roles. Assignments and queues route inbound to the right people.' },
  { n: 3, title: 'Wire up automation',body: 'Point-and-click auto-reply, VIP rules, business hours, and CRM sync — no code needed.' },
  { n: 4, title: 'Measure & iterate', body: 'Dashboards, scheduled PDFs, and AI insights show what worked. Adjust routing without redeploying anything.' },
];

const FAQ = [
  { q: 'Do we need to install anything?', a: 'No. SnapText is web-based with optional native iOS and Android apps. Agents open the browser (or the app) and are ready to text.' },
  { q: 'Can we bring our existing business number?', a: 'Yes. We port existing local numbers and text-enable landlines, or provision new 10DLC and toll-free numbers.' },
  { q: 'How is opt-out handled?', a: 'STOP, START, and HELP are handled automatically with an audit trail on every conversation. Blasts enforce opt-out before send.' },
  { q: 'What compliance rails ship in the box?', a: 'SHAFT content filtering (sex, hate, alcohol, firearms, tobacco), configurable footer text, 24-hour dedupe on auto-replies, and a full activity log on state changes.' },
  { q: 'Which CRMs do you integrate with?', a: 'Salesforce, Clio, Meevo, and ShipStation ship today, with Microsoft Teams for team notifications. New CRM connectors are added regularly — if the one you use isn\'t on the list yet, ask us. Webhooks handle anything not covered by a native connector.' },
  { q: 'How does routing work when everyone is busy?', a: 'Configure a fallback strategy per number: send to a pool, next-available user, a specific fallback user, or auto-reply only.' },
];

const Features = () => {
  useEffect(() => {
    document.title = 'SnapText — Platform capabilities';
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', 'description'); document.head.appendChild(tag); }
    tag.setAttribute('content', 'SnapText is a business SMS platform with shared inbox, routing, blasts, and native CRM integrations.');
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <MarketingNav variant="full" />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10 text-center">
        <div className="text-xs font-semibold tracking-wider uppercase mb-4" style={{ color: BRAND_BLUE }}>Business SMS, built for teams</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05]" style={{ textWrap: 'balance' }}>
          Every text your business sends — in one shared inbox.
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-lg text-slate-600 leading-relaxed" style={{ textWrap: 'balance' }}>
          SnapText turns SMS into a real workflow. Shared conversations, point-and-click routing, personalized blasts, SHAFT compliance, and native CRM sync — on any number your business already uses.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCTA>Request access</PrimaryCTA>
          <SecondaryCTA to="/onepager">See the one-pager</SecondaryCTA>
        </div>

        {/* Product screenshot immediately under hero */}
        <div className="mt-10">
          <ScreenshotFrame>
            <InboxMockup />
          </ScreenshotFrame>
        </div>
      </section>

      {/* INTEGRATION STRIP #1 */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <IntegrationStrip />
      </section>

      {/* METRIC CALLOUTS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <MetricStat number="1 inbox" label="Your whole team on every number, in one place" />
          <MetricStat number="No IT"   label="Set up your numbers and your team from the app — no engineers or consultants" />
          <MetricStat number="100%"    label="Opt-outs and content rules handled for you, on every send" />
        </div>
      </section>

      {/* WHAT YOU GET — feature grid */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="What you get"
            title="Every feature the modern business messaging team needs."
            subtitle="Not a bare SMS gateway — SnapText ships the workflows, guardrails, and analytics your operators, agents, and admins actually use every day." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPABILITY_GRID.map((c) => (
              <div key={c.title} className="p-5 rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{c.icon}</span>
                  <h3 className="text-base font-semibold text-slate-900">{c.title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROUTING DEEP DIVE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{ color: BRAND_BLUE }}>Automation & routing</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{ textWrap: 'balance' }}>
              Route every message to the right person, automatically.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              A visual routing wizard on every number. Pick dedicated, round-robin, least-loaded, or queue-based routing. Layer on VIP rules, sticky assignments, time-of-day switches, and keyword overrides — no code, no redeploy.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Auto-reply and business-hours messaging built in</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Overflow fallbacks: pool, next-available, specific user, or auto-reply only</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Queues with SLAs, alerts, and auto-escalation</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Full routing audit log per number</li>
            </ul>
          </div>
          <ScreenshotFrame caption="Routing wizard — one screen per number, no code">
            <RoutingMockup />
          </ScreenshotFrame>
        </div>
      </section>

      {/* BLASTS + DRIPS DEEP DIVE */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <ScreenshotFrame caption="Text blast composer — personalized, compliance-checked">
            <BlastMockup />
          </ScreenshotFrame>
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{ color: BRAND_BLUE }}>Text blasts</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{ textWrap: 'balance' }}>
              Broadcast at scale without breaking compliance.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Segment contact lists, personalize with placeholders, and send now or later. SHAFT filters, opt-out lists, and 10DLC segment counts run automatically before anything leaves your number.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> One-off blasts to any list</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Personalization tokens per contact — first name, custom fields</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Auto-throttled to respect carrier limits</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Every send tied back to a contact for full history</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ANALYTICS DEEP DIVE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{ color: BRAND_BLUE }}>Analytics & AI insights</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{ textWrap: 'balance' }}>
              Know what's working — without building a data team.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Customizable dashboard widgets, response-time metrics, and AI-generated summaries of volume and sentiment. Schedule PDF reports on any cadence.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> First-response, average-response, SLA breach tracking</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Per-user, per-number, and per-queue breakdowns</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> AI-generated summaries and sentiment tagging</li>
              <li className="flex gap-2"><span style={{ color: BRAND_BLUE }}>▸</span> Scheduled PDF delivery to any inbox</li>
            </ul>
          </div>
          <ScreenshotFrame caption="Dashboard widgets — pick, arrange, and share">
            <AnalyticsMockup />
          </ScreenshotFrame>
        </div>
      </section>

      {/* INTEGRATION STRIP #2 + per-integration section */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Integrations"
            title="Native connections to the systems you already run."
            subtitle="Direct integrations for the platforms your business lives in — and more are added all the time. Don't see yours? Let us know. Outbound webhooks handle anything not covered by a native connector." />
          <IntegrationStrip label={null} />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INTEGRATION_DETAILS.map((i) => {
              const meta = INTEGRATIONS.find(x => x.name === i.name);
              return (
                <div key={i.name} className="p-5 rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta?.color || BRAND_BLUE }} />
                    <h3 className="text-base font-semibold text-slate-900">{i.name}</h3>
                    <span className="ml-auto text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{meta?.category}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{i.what}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MOBILE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <ScreenshotFrame caption="Native iOS + Android — push notifications and full parity">
            <MobileMockup />
          </ScreenshotFrame>
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase mb-3" style={{ color: BRAND_BLUE }}>Mobile</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{ textWrap: 'balance' }}>
              Your inbox in your pocket.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Native iOS and Android apps built on Capacitor. Push notifications route the same as the web app, so your on-call agent gets the ping the second an inbound arrives.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="How it works" title="Live in days, not months." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} className="p-5 rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm mb-3" style={{ backgroundColor: BRAND_BLUE }}>{s.n}</div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHeader eyebrow="FAQ" title="Frequently asked questions" />
        <div className="space-y-4">
          {FAQ.map((f, i) => (
            <details key={i} className="group rounded-xl border border-slate-200 bg-white p-5">
              <summary className="cursor-pointer flex items-center justify-between text-base font-semibold text-slate-900 list-none">
                <span>{f.q}</span>
                <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-2xl p-10 sm:p-14 text-center text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #003D80)` }}>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ textWrap: 'balance' }}>See SnapText on your own numbers.</h2>
          <p className="mt-3 text-white/85 leading-relaxed max-w-xl mx-auto">Bring your team, keep your numbers, and start texting like a modern operation.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="mailto:hello@snapcom.com?subject=SnapText%20-%20Request%20access&body=I'd%20like%20to%20see%20SnapText%20on%20our%20numbers."
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white font-semibold shadow-sm hover:bg-slate-50"
               style={{ color: BRAND_BLUE }}>
              Request access
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <a href="/onepager" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/40 text-white font-semibold hover:bg-white/10">See the one-pager</a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Features;
